import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { DDSLoader } from 'three/examples/jsm/loaders/DDSLoader'
import { TWEEN } from 'three/examples/jsm/libs/tween.module.min'

class ObjThreeModelSingleton {
  constructor (props) {

    let self = this
    let defaults = {
      modelPath: '/models/obj/TableWeb.obj',
      materialPath: '/models/obj/TableWeb.mtl',
      enableZoom: false,
      zoomFactor: 1,
      position: {
        x: 250,
        y: 300,
        z: 600,
      },
    }

    this.options = Object.assign(defaults, props)

    this.root = {}
    this.canvas = props.canvas
    this.modelPath = this.options.modelPath
    this.materialPath = this.options.materialPath
    this.percentComplete = 0
    this.failed = false

    this.canvasWidth = 700
    this.canvasHeight = 650
    this.aspectRatio = this.canvasWidth / this.canvasHeight

    this.scene = new THREE.Scene()

    let canvas = this.canvas

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
    // this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.setPixelRatio(1)
    this.renderer.pixelRatio = 1
    this.renderer.setSize(this.canvasWidth, this.canvasHeight)
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.shadowMap.autoUpdate = true
    this.renderer.setClearColor(0xffffff, 0)

    const fov = 20
    const near = 20
    const far = 1000

    this.camera = new THREE.PerspectiveCamera(fov, self.aspectRatio, near, far)
    this.camera.position.z = this.options.position.z
    this.camera.position.x = this.options.position.x
    this.camera.position.y = this.options.position.y

    this.windowHalfX = window.innerWidth / 2
    this.windowHalfY = window.innerHeight / 2

    this.controls = new OrbitControls(this.camera, this.canvas)
    this.controls.enableZoom = this.options.enableZoom
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.05
    this.controls.rotateSpeed = 0.4
    this.controls.autoRotate = false
    this.controls.enablePan = false
    this.controls.target.set(0, 5, 0)
    this.controls.update()
    this.materialsArr = []
    this.materialsCloudsArr = []
    this.animationMixer = null
    this.clock = new THREE.Clock();

    this.materials = {}

    this.camera.fov *= (1 / this.options.zoomFactor)
    this.camera.updateProjectionMatrix()

    this.qualityCoef = 1

    /*Objects params*/
    this.cloudsRadius = 21.4 * this.qualityCoef
    this.cloudsVert = 32 * this.qualityCoef
    this.planetRadius = 20 * this.qualityCoef
    this.planetVert = 600 * this.qualityCoef

    if(window.innerWidth < 768) {
      this.planetVert = 500
    }

    if (!ObjThreeModelSingleton._instance) {
      ObjThreeModelSingleton._instance = this
    }

    return ObjThreeModelSingleton._instance
  }

  static getInstance () {
    return this._instance
  }

  initModel () {
    let self = this

    this.init()
    animate()

    function animate () {
      requestAnimationFrame(animate)
      self.controls.update()
      self.render()

      const delta = self.clock.getDelta();

      self.animationMixer.update( delta );

      TWEEN.update()
    }
  }

  init () {

    // scene
    let self = this

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3)
    this.scene.add(ambientLight)

    const light = new THREE.SpotLight(0xffffff, 2.7)
    light.castShadow = true
    light.position.set(-5000, 4000, -500)
    light.angle = 0.06
    light.penumbra = 0
    light.distance = 20000
    light.shadow.mapSize.width = 512  // default
    light.shadow.mapSize.height = 512 // default
    this.light = light
    this.scene.add(light.target)
    this.camera.add(light)

    const light2 = new THREE.SpotLight(0xffffff, 2)
    light2.castShadow = true
    light2.position.set(800, -300, -3400)
    light2.angle = 1
    light2.penumbra = 0
    light2.distance = 20000
    light2.shadow.mapSize.width = 512  // default
    light2.shadow.mapSize.height = 512 // default
    this.light2 = light2
    this.scene.add(light2.target)
    this.camera.add(light2)

    this.scene.add(this.camera)

    const onProgress = function (xhr) {

      if (xhr.lengthComputable) {

        const percentComplete = xhr.loaded / xhr.total * 100
        // console.log(Math.round(percentComplete) + '% downloaded');

        self.percentComplete = Math.round(percentComplete)

      }
    }

    const manager = new THREE.LoadingManager()
    manager.addHandler(/\.dds$/i, new DDSLoader())

    manager.onLoad = function () {

      self.percentComplete = 101

    }

    const geometrySatellite = new THREE.SphereGeometry(1.8 * this.qualityCoef, 16, 16)
    const geometrySatellite2 = new THREE.SphereGeometry(1.2 * this.qualityCoef, 16, 16)

    const geometry = new THREE.SphereBufferGeometry(self.planetRadius, self.planetVert, self.planetVert)
    const geometryClouds = new THREE.SphereBufferGeometry(self.cloudsRadius, self.cloudsVert, self.cloudsVert)

    geometry.receiveShadow = true
    geometry.castShadow = true

    let materialsLength = 9

    for (let i = 1; i <= materialsLength; i++) {
      let loader = new THREE.TextureLoader()

      let materialItem = new THREE.MeshPhongMaterial({
        map: loader.load(require('../../assets/img/model/Stage_' + i + '_Diff.jpg')),
      })

      materialItem.flatShading = true
      materialItem.bumpMap = new THREE.TextureLoader().load(require('../../assets/img/model/topo.jpg'))
      materialItem.normalMap = new THREE.TextureLoader().load(require('../../assets/img/model/normal.jpg'))
      materialItem.displacementMap = new THREE.TextureLoader().load(require('../../assets/img/model/displacement.jpg'))
      materialItem.displacementScale = 2
      materialItem.displacementBias = 0.2
      materialItem.metalness = 0
      materialItem.roughness = 3
      materialItem.shininess = 2

      if(i > 1) {
        materialItem.opacity = 1
      }

      self.materialsArr.push(materialItem)
    }

    let materialsCloudsLength = 9

    for (let i = 4; i <= materialsCloudsLength; i++) {
      let loader = new THREE.TextureLoader()

      let materialItem = new THREE.MeshPhongMaterial({
        alphaMap: loader.load(require('../../assets/img/model/Stage_' + i + '_Atmo.jpg')),
      })

      materialItem.specular = new THREE.Color(0, 0 ,0)
      materialItem.emissive = new THREE.Color(0, 0 ,0)
      materialItem.transparent = true
      materialItem.metalness = 0
      materialItem.roughness = 3
      materialItem.shininess = 2

      self.materialsCloudsArr.push(materialItem)
    }

    const materialClouds = self.materialsCloudsArr[0]

    materialClouds.opacity = 0
    materialClouds.transparent = true

    const satellite = new THREE.Mesh(geometrySatellite, new THREE.MeshPhongMaterial({
      map: new THREE.TextureLoader().load(require('../../assets/img/model/Stage_6_Diff.jpg')),
      depthTest: true,
    }))

    const satellite2 = new THREE.Mesh(geometrySatellite2, new THREE.MeshPhongMaterial({
      map: new THREE.TextureLoader().load(require('../../assets/img/model/Stage_6_Diff.jpg')),
      depthTest: true,
    }))

    const sphere = new THREE.Mesh(geometry, self.materialsArr[0])
    const sphereClouds = new THREE.Mesh(geometryClouds, materialClouds)

    sphere.castShadow = true
    sphere.receiveShadow = true
    sphere.name = 'Sphere'
    satellite.name = 'Satellite'
    sphereClouds.name = 'Clouds'

    satellite.position.x = 17 * this.qualityCoef
    satellite.position.y = -7 * this.qualityCoef
    satellite.position.z = 17 * this.qualityCoef

    satellite2.position.x = 18 * this.qualityCoef
    satellite2.position.y = 0 * this.qualityCoef
    satellite2.position.z = 18 * this.qualityCoef

    self.sphereGroup = new THREE.Group()
    self.sphere = sphere
    self.sphereGroup.add(self.sphere)

    self.satellite = satellite
    self.satellite2 = satellite2

    self.cloudsGroup = new THREE.Group()
    self.clouds = sphereClouds
    self.cloudsGroup.add(self.clouds)

    const group = new THREE.Group()
    group.add(self.sphereGroup)
    group.add(self.cloudsGroup)
    group.add(self.satellite)
    group.add(self.satellite2)

    self.scene.add(group)

    self.root = group
    self.animationMixer = new THREE.AnimationMixer(self.root)

    self.sphere.material.transparent = true
    self.clouds.material.transparent = true

    // console.log('self.sphere.material', self.sphere.material)

    self.setRotationCenter(sphere)

    const box = new THREE.Box3().setFromObject(self.sphereGroup)
    const boxSize = box.getSize(new THREE.Vector3()).length()
    const boxCenter = box.getCenter(new THREE.Vector3())
    self.controls.maxDistance = boxSize * 10
    self.controls.target.copy(boxCenter)
    self.controls.update()

    document.addEventListener('mousemove', (e) => {
      self.onDocumentMouseMove(e, self)
    })

    window.addEventListener('resize', (e) => {
      self.onWindowResize(e, self)
    })

  }

  onWindowResize (event, self) {
    this.windowHalfX = window.innerWidth / 2
    this.windowHalfY = window.innerHeight / 2

    this.camera.aspect = self.aspectRatio
    this.camera.updateProjectionMatrix()

    this.renderer.setSize(self.canvasWidth, self.canvasHeight)
  }

  onDocumentMouseMove (event, self) {
    self.mouseX = (event.clientX - self.windowHalfX) / 2
    self.mouseY = (event.clientY - self.windowHalfY) / 2
  }

  render () {
    if(this.satellite !== undefined) {
      const vector1 = new THREE.Vector3(this.sphere.position.x, this.sphere.position.y, this.sphere.position.z)
      const vector2 = new THREE.Vector3(0, 1, 0)
      const vector3 = new THREE.Vector3(0.02, 1, 0)

      this.rotateAboutPoint(
        this.satellite,
        vector1,
        vector2,
        THREE.Math.degToRad(0.7)
      )

      this.rotateAboutPoint(
        this.satellite2,
        vector1,
        vector3,
        THREE.Math.degToRad(0.4)
      )
    }

    if(this.sphere !== undefined) {
      this.sphereGroup.rotation.y += 0.002
    }

    if(this.clouds !== undefined) {
      this.cloudsGroup.rotation.y += 0.001
    }

    // this.camera.lookAt(this.scene.position)
    this.renderer.render(this.scene, this.camera)
  }

  setRotationCenter (object) {
    this.box = new THREE.Box3().setFromObject(object)
    this.box.getCenter(object.position) // this re-sets the mesh position
    object.position.multiplyScalar(-1)

    // let pivot = new THREE.Group()
    // this.scene.add(pivot)
    // pivot.add(object)
  }

  rotateAboutPoint(obj, point, axis, theta, pointIsWorld){
    pointIsWorld = (pointIsWorld === undefined)? false : pointIsWorld;

    if(pointIsWorld){
      obj.parent.localToWorld(obj.position); // compensate for world coordinate
    }

    obj.position.sub(point); // remove the offset
    obj.position.applyAxisAngle(axis, theta); // rotate the POSITION
    obj.position.add(point); // re-add the offset

    if(pointIsWorld){
      obj.parent.worldToLocal(obj.position); // undo world coordinates compensation
    }

    obj.rotateOnAxis(axis, theta); // rotate the OBJECT
  }
}

export default ObjThreeModelSingleton
